import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.store';
import { api } from '../services/api';
import SecretSantaMessagePopup from './SecretSantaMessagePopup';
import { MessageWithSender } from '../types/message';

const STORAGE_KEY = 'secret_santa_shown_messages';

const getShownMessageIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (error) {
    console.error('Failed to read shown message IDs from localStorage:', error);
  }
  return new Set();
};

const addShownMessageId = (messageId: string): void => {
  try {
    const shownIds = getShownMessageIds();
    shownIds.add(messageId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(shownIds)));
  } catch (error) {
    console.error('Failed to save shown message ID to localStorage:', error);
  }
};

const SecretSantaMessageNotifier: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  const [currentMessage, setCurrentMessage] = useState<(MessageWithSender & { group_name?: string }) | null>(null);
  const [unreadMessages, setUnreadMessages] = useState<(MessageWithSender & { group_name?: string })[]>([]);
  const shownIdsRef = useRef<Set<string>>(getShownMessageIds());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchUnreadMessages = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get<(MessageWithSender & { group_name?: string })[]>('/messages/unread-secret-santa');
      const messages = response.data || [];
      
      // Filter out messages that have already been shown
      const newMessages = messages.filter(
        (msg) => !shownIdsRef.current.has(msg.id)
      );

      setUnreadMessages(newMessages);
    } catch (error) {
      console.error('Failed to fetch unread Secret Santa messages:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentMessage(null);
      setUnreadMessages([]);
      return;
    }

    // Initial fetch
    fetchUnreadMessages();

    // Set up interval to check for new messages every 10 seconds
    checkIntervalRef.current = setInterval(() => {
      fetchUnreadMessages();
    }, 10000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isAuthenticated]);

  // Show next message when current one is dismissed or viewed
  useEffect(() => {
    if (!currentMessage && unreadMessages.length > 0) {
      // Show the most recent unread message (first in array since they're sorted DESC)
      const nextMessage = unreadMessages[0];
      setCurrentMessage(nextMessage);
    }
  }, [currentMessage, unreadMessages]);

  const handleDismiss = () => {
    if (currentMessage) {
      // Mark as shown
      addShownMessageId(currentMessage.id);
      shownIdsRef.current.add(currentMessage.id);
      
      // Remove from unread messages
      setUnreadMessages((prev) => prev.filter((msg) => msg.id !== currentMessage.id));
      setCurrentMessage(null);
    }
  };

  const handleView = () => {
    if (currentMessage && currentMessage.secret_santa_group_id) {
      // Mark as shown
      addShownMessageId(currentMessage.id);
      shownIdsRef.current.add(currentMessage.id);
      
      // Remove from unread messages
      setUnreadMessages((prev) => prev.filter((msg) => msg.id !== currentMessage.id));
      setCurrentMessage(null);
      
      // Navigate to the message
      navigate(`/messages?groupId=${currentMessage.secret_santa_group_id}&role=receiver`);
    }
  };

  if (!currentMessage) {
    return null;
  }

  return (
    <SecretSantaMessagePopup
      message={currentMessage}
      onDismiss={handleDismiss}
      onView={handleView}
    />
  );
};

export default SecretSantaMessageNotifier;

